import React from 'react';
import { View, Pressable, Platform } from 'react-native';
import { Text } from 'tamagui';
import { FontAwesome } from '@expo/vector-icons';
import { useSelector } from 'react-redux';
import { DefaultRootState } from '@reduxLocal/persistState';
import styles from '@stylesheet';
import { responsiveFontSize } from '@utils/responsiveFontSize';
import { useTranslation } from 'react-i18next';

type WhatsAppSignUpButtonProps = {
  onPress: () => void;
};

/**
 * OAuth-style pill button for "Register with WhatsApp" — matches EmailSignUpButton layout.
 */
const WhatsAppSignUpButton = ({ onPress }: WhatsAppSignUpButtonProps) => {
  const { t } = useTranslation();
  const theme = useSelector((state: DefaultRootState) => state.app.theme.lightTheme);
  const buttonColors = {
    background: theme ? '#ffffff' : 'rgba(30, 41, 59, 0.95)',
    text: theme ? '#111827' : '#e2e8f0',
    border: theme ? '#e5e7eb' : 'rgba(148, 163, 184, 0.35)',
    hover: theme ? 'rgba(37, 211, 102, 0.18)' : 'rgba(37, 211, 102, 0.22)',
    shadow: theme ? '#000000' : '#020617',
    icon: '#25D366',
  };

  const outerStyle = {
    minHeight: Platform.select({ web: 50, default: 50 }),
    height: 50,
    maxHeight: 50,
    width: '100%' as const,
    maxWidth: Platform.OS === 'web' ? 400 : undefined,
    alignSelf: Platform.OS === 'web' ? ('center' as const) : undefined,
    flex: Platform.OS === 'web' ? 0 : 1,
    elevation: 1,
    shadowRadius: 10,
    shadowOpacity: 0.2,
    backgroundColor: buttonColors.background,
    borderRadius: 30,
    borderWidth: 1,
    borderColor: buttonColors.border,
    shadowColor: buttonColors.shadow,
    flexDirection: 'row' as const,
    overflow: 'hidden' as const,
    justifyContent: 'center' as const,
    alignContent: 'center' as const,
    alignItems: 'stretch' as const,
  };

  return (
    <Pressable onPress={onPress} style={outerStyle} testID="whatsappSignUpButton">
      {({ hovered }) => (
        <View style={{ flex: 1, width: '100%' }}>
          <View
            style={{
              paddingHorizontal: '10%',
              flex: 1,
              flexDirection: 'row',
              backgroundColor: hovered ? buttonColors.hover : 'rgba(37,211,102,0)',
              borderRadius: 10,
            }}
          >
            <View style={[styles.centerEverything, { flex: 1, maxWidth: 40 }]}>
              <FontAwesome name="whatsapp" size={22} color={buttonColors.icon} />
            </View>
            <View
              style={{
                flex: 4,
                justifyContent: 'center',
                alignItems: 'center',
                minWidth: Platform.OS === 'web' ? 0 : 220,
              }}
            >
              <Text
                adjustsFontSizeToFit
                allowFontScaling={false}
                style={{
                  fontSize: responsiveFontSize(15),
                  color: buttonColors.text,
                  fontWeight: '600',
                }}
              >
                {t('Register with WhatsApp')}
              </Text>
            </View>
          </View>
        </View>
      )}
    </Pressable>
  );
};

export default WhatsAppSignUpButton;
